import {Language} from '../../i18nTypes';
import {HibuSearchRequest, HibuTag, HibuTags, ProductDocument, SearchRequest} from '../../search/search-types';
import {AbortCategory, postJsonWithBody, postJsonWithBodyForStream} from '../../services/fetcher';
import {v5 as uuidv5} from 'uuid';
import {
    AiAssistantContextDocument,
    AiAssistantConversation,
    AiAssistantConversationContext,
    AiAssistantMessage,
    AiAssistantRequest,
    AiAssistantResponse,
    AiAssistantSuggestions,
    ChatType,
    DEFAULT_ASSISTANT_CONVERSATION_CONTEXT,
    HibuAssistantMessage,
    HibuTagDescription,
    HibuTagValue,
    Role,
    StreamHandlers,
    TotalDuplicates,
} from './types';

export const HIBU_ASSISTANT_ENDPOINT = '/hibu-assistant';
export const GENERATE_STREAM_ENDPOINT = '/generate/stream';
export const SUGGEST_ENDPOINT = '/suggest';
export const TOTAL_DUPLICATES_ENDPOINT = '/totalDuplicates';

export function getEmptyConversation(
    type: ChatType,
    conversationId: string,
    role: Role.ASSISTANT | Role.USER,
    message: string,
    totalDuplicatesCount: number,
    searchRequest: HibuSearchRequest | undefined,
): AiAssistantConversation {
    return {
        type: type,
        id: conversationId,
        conversationMessages: [
            {
                role: role,
                text: message,
            },
        ],
        searchResultsRequests: [searchRequest],
        suggestions: [],
        summary: '',
        created: new Date(),
        totalDuplicatesCount: totalDuplicatesCount || 0,
        context: DEFAULT_ASSISTANT_CONVERSATION_CONTEXT,
    };
}

export const handleCopyOnClick = (event, text: string, text2: string): void => {
    const element = event.target as HTMLAnchorElement;
    const originalText = element.textContent;

    copyToClipboard(text).then(() => {
        element.textContent = text2; // set the text to "Kopiert"
        setTimeout(() => {
            element.textContent = originalText; // restore the original text after 2 seconds
        }, 2000);
    });
};

export async function copyToClipboard(text: string) {
    if ('clipboard' in navigator) {
        return await navigator.clipboard.writeText(text);
    }
}

const mapToHibuAssistantMessage = ({answer = '', documents = []}: AiAssistantResponse): HibuAssistantMessage => {
    return {
        text: answer,
        role: Role.ASSISTANT,
        metadata: {
            documents: documents,
        },
    };
}

function extractConversation(conversationMessages: HibuAssistantMessage[]): AiAssistantMessage[] {
    return conversationMessages.map((c) => {
        const sources: string[] = c.metadata?.documents?.map((doc) => doc.id) || [];
        return new AiAssistantMessage(c.text, c.role, sources);
    });
}

export function getHibuAssistantRequestForSummary(
    documents: AiAssistantContextDocument[],
    uiLanguage: Language,
    message: string,
    conversationContext: AiAssistantConversationContext,
): AiAssistantRequest {
    return new AiAssistantRequest(
        message,
        uiLanguage,
        [], // without conversation for summary generation
        conversationContext,
        documents,
    );
}

export function getHibuAssistantRequestWithDocuments(
    documents: AiAssistantContextDocument[],
    uiLanguage: Language,
    message: string,
    currentConversation: AiAssistantConversation,
    conversationContext: AiAssistantConversationContext,
): AiAssistantRequest {
    return getHibuAssistantRequest(message, uiLanguage, currentConversation, documents, undefined, conversationContext);
}

export function getHibuAssistantRequestWithSearchRequest(
    hibuSearchRequest: HibuSearchRequest,
    uiLanguage: Language,
    message: string,
    currentConversation: AiAssistantConversation,
    conversationContext: AiAssistantConversationContext,
): AiAssistantRequest {
    return getHibuAssistantRequest(
        message,
        uiLanguage,
        currentConversation,
        undefined,
        hibuSearchRequest,
        conversationContext,
    );
}

export function getHibuAssistantRequest(
    message: string,
    uiLanguage: Language,
    currentConversation: AiAssistantConversation,
    documents: AiAssistantContextDocument[] | undefined,
    hibuSearchRequest: HibuSearchRequest | undefined,
    conversationContext: AiAssistantConversationContext,
): AiAssistantRequest {
    return new AiAssistantRequest(
        message,
        uiLanguage,
        extractConversation(currentConversation.conversationMessages),
        conversationContext,
        documents,
        hibuSearchRequest,
    );
}

function extracTagStringValues(t: HibuTag) {
    if (t.extra) {
        const stringValue = t.extra.value_tokenized_string;
        if (stringValue) {
            return stringValue;
        }
    }
    return null;
}

export const extractSupportedTagsOnly = (
    documentTags: HibuTags,
    supportedTags: HibuTagDescription[],
): HibuTagValue[] => {
    if (!documentTags) {
        return [];
    }

    const supportedTagsNames = supportedTags.map((tag) => tag.name);
    const tagValues: HibuTagValue[] = [];
    for (const supportedTagsName of supportedTagsNames) {
        const tag: HibuTag[] = documentTags[supportedTagsName];
        if (!tag) {
            continue;
        }
        const tagStringValues: string[] = tag.map((t) => extracTagStringValues(t)).filter((t) => t !== null);
        const uniqueTagStringValues = Array.from(new Set(tagStringValues));
        tagValues.push(new HibuTagValue(supportedTagsName, uniqueTagStringValues));
    }
    return tagValues;
};

export async function fetchSummary(
    selectedDocument: ProductDocument,
    currentLanguage: Language,
    conversationContext: AiAssistantConversationContext,
    onToken: (token: string) => void,
) {
    const document = new AiAssistantContextDocument(selectedDocument.id, selectedDocument.title, []);
    const request = getHibuAssistantRequestForSummary(
        [document],
        currentLanguage,
        'Give a short summary of the document, naming the subject for example the person or product or problem and importantly what the document is about.',
        conversationContext,
    );
    return await fetchStreamedAiResponse(request, {
        onToken: (token) => onToken(token),
    });
}

function isBackendErrorResponse(answer: string | undefined, documents: AiAssistantContextDocument[]): boolean {
    if (!answer) {
        return false;
    }

    return answer.startsWith('Error:') && documents.length === 0;
}

export async function fetchStreamedAiResponse(
    request: AiAssistantRequest,
    handlers: StreamHandlers,
): Promise<HibuAssistantMessage> {
    const response = await postJsonWithBodyForStream(
        HIBU_ASSISTANT_ENDPOINT + GENERATE_STREAM_ENDPOINT,
        JSON.stringify(request),
        AbortCategory.AI_GENERATE,
    );
    const aiAssistantResponse: AiAssistantResponse = await parseStreamedResponse(response, handlers);
    return mapToHibuAssistantMessage(aiAssistantResponse);
}

async function parseStreamedResponse(response: Response, handlers: StreamHandlers) {
    let answer: string = '';
    let documents: AiAssistantContextDocument[] | [] = [];
    let hasReceivedDocuments = false;
    const reader = response.body?.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    while (true) {
        const {done, value} = await reader!.read();
        if (done) break;
        buffer += decoder.decode(value, {stream: true});

        const lines = buffer.split('\n'); // split line by line (NDJSON)
        buffer = lines.pop()!; // last line could be incomplete, so keep in buffer
        for (const line of lines) {
            if (!line.trim()) continue;

            let parsed: AiAssistantResponse;
            try {
                const jsonString = line.replace(/^data:\s*/, '');
                if (jsonString.trim() === '[DONE]') continue;
                parsed = JSON.parse(jsonString);
            } catch (err) {
                console.error('Fehler beim Parsen der Zeile:', line, err);
                handlers.onError?.(err);
                throw err;
            }

            if (isBackendErrorResponse(parsed.answer, parsed.documents)) {
                console.error('Backend error in streaming response:', parsed.answer);
                const error = new Error(parsed.answer);
                handlers.onError?.(error);
                throw error;
            }

            handlers.onToken(parsed.answer); // needed for displaying the answer in a streamed manner
            answer += parsed.answer; // needed for adding the answer to the conversation state
            if (!hasReceivedDocuments) {
                documents = parsed.documents; // documents are static and therefore need only to be set once
                hasReceivedDocuments = true;
            }
        }
    }

    return {answer, documents: documents};
}

export async function fetchSuggestions(
    documents: AiAssistantContextDocument[],
    uiLanguage: Language,
    currentConversation: AiAssistantConversation,
    conversationContext: AiAssistantConversationContext,
) {
    try {
        const message = 'Suggest at least 3 questions. Do not repeat already posed questions.';
        const llmBackendDocumentsRequest = getHibuAssistantRequestWithDocuments(
            documents,
            uiLanguage,
            message,
            currentConversation,
            conversationContext,
        );
        const response: AiAssistantSuggestions = await postJsonWithBody<AiAssistantSuggestions>(
            HIBU_ASSISTANT_ENDPOINT + SUGGEST_ENDPOINT,
            JSON.stringify(llmBackendDocumentsRequest),
            AbortCategory.AI_SUGGESTION,
        );

        return response.suggestions; // Return the suggestions array
    } catch (error) {
        console.error('Failed to fetch:', error);
        return []; // Return an empty array in case of error
    }
}

export async function fetchTotalDuplicatesCount(request: SearchRequest): Promise<number> {
    const response: TotalDuplicates = await postJsonWithBody<TotalDuplicates>(
        HIBU_ASSISTANT_ENDPOINT + TOTAL_DUPLICATES_ENDPOINT,
        JSON.stringify(request),
        AbortCategory.AI_SUGGESTION,
    );
    return response.totalDuplicatesCount;
}

// Uuid Generation //

const NAMESPACE_UUID = '123e4567-e89b-12d3-a456-426614174000';

export function getUuidForSearchRequest(searchRequest?: HibuSearchRequest): string {
    let concatenatedAttributes: string;
    if (!searchRequest) {
        concatenatedAttributes = ['files', 0, 24, '{field: title, order: asc}'].join('|'); // Use a delimiter to separate values
    } else {
        concatenatedAttributes = [
            searchRequest.container,
            searchRequest.displayLanguage,
            searchRequest.query,
            searchRequest.from,
            searchRequest.size,
            JSON.stringify(searchRequest.filters), // Convert objects to strings
            searchRequest.sort,
            searchRequest.spellcheckEnabled,
            JSON.stringify(searchRequest.spellcheckParams),
            JSON.stringify(searchRequest.facetFields),
            JSON.stringify(searchRequest.queryFields),
            JSON.stringify(searchRequest.returnFields),
        ].join('|');
    }

    return uuidv5(concatenatedAttributes, NAMESPACE_UUID);
}

export function getUuidForSingleDocument(source: string): string {
    return uuidv5(source, NAMESPACE_UUID);
}

export function getLastConversationId(conversations: AiAssistantConversation[], type: ChatType): string {
    conversations = conversations.sort((c1, c2) => new Date(c2.created).getTime() - new Date(c1.created).getTime());
    const lastConversation = conversations.filter(
        (c) => c.type === type && c.conversationMessages.find((m) => m.role === Role.USER),
    )[0];
    return lastConversation ? lastConversation.id : uuidv4();
}

//todo: use existing npm package
export function uuidv4() {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
        (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
    );
}
